/**
 * TypeScript view of the `boggle` format.
 * Source of truth: `shared/schema/boggle.{definition,submission,solution,progress}.json`.
 * The constants below are the same literals as `backend/apps/puzzletypes/boggle/tray.py`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "en" | "fr";

export const LANGUAGES: Language[] = ["en", "fr"];
export const SIZES = [4, 5] as const;
export type Size = (typeof SIZES)[number];
export const DEFAULT_MIN_LENGTH = 3;
export const MIN_LENGTHS = [3, 4, 5] as const;
export const MAX_WORDS = 500;
export const MAX_WORD_LENGTH = 32;

/** A die face: one uppercase letter, or `QU`, which reads two letters. */
export type Face = string;
export type Cell = [row: number, col: number];

export interface BoggleDefinition {
  puzzleType: "boggle";
  typeVersion: number;
  language: Language;
  size: Size;
  /** `size` rows of `size` faces. */
  grid: Face[][];
  /** Shortest word accepted, in letters (a QU face counts two). 3 when absent. */
  minLength?: number;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** Why a word does not count. The first two are visible rules; the rest come from the server. */
export type WordIssue = "too_short" | "not_on_grid";

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface BoggleProgress {
  version: 1;
  /** The words found so far, normalised, in the order they were found. */
  words: string[];
  /** Words the server refused, with the reason code it gave (folded in by `applyVerdict`). */
  rejected?: Record<string, string>;
}

export interface BoggleSubmission {
  words: string[];
}

/** A few words the author found on the tray, proving it is playable. Never read by the verdict. */
export interface BoggleSolution {
  words: string[];
}

export function minLengthOf(definition: BoggleDefinition): number {
  return typeof definition.minLength === "number" ? definition.minLength : DEFAULT_MIN_LENGTH;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
